import { useMemo, useState } from 'react'
import { useOutboxStore, OUTBOX_KIND_LABEL, type OutboxItem } from '@/features/offline/model/outboxStore'
import { Button } from '@/shared/ui/Button'
import { AlertCircleIcon } from '@/shared/ui/icons'

const statusLabel: Record<OutboxItem['status'], string> = {
  pending: 'Pendente',
  running: 'Enviando',
  failed: 'Falhou',
}

const pluralize = (value: number, singular: string, plural: string): string =>
  value === 1 ? singular : plural

export const OutboxSyncIndicator = () => {
  const items = useOutboxStore((state) => state.items)
  const isRetrying = useOutboxStore((state) => state.isRetrying)
  const retryAll = useOutboxStore((state) => state.retryAll)
  const clearFailed = useOutboxStore((state) => state.clearFailed)
  const remove = useOutboxStore((state) => state.remove)
  const [open, setOpen] = useState(false)

  const summary = useMemo(() => {
    const failed = items.filter((item) => item.status === 'failed').length
    const pending = items.filter((item) => item.status === 'pending' || item.status === 'running').length
    return { failed, pending, total: items.length }
  }, [items])

  if (!items.length) return null

  return (
    <div
      className="fixed bottom-24 right-4 z-50 md:bottom-6 md:right-6"
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        onClick={() => setOpen((state) => !state)}
        className={`inline-flex min-h-11 items-center gap-2 rounded-2xl border px-3 text-xs font-semibold shadow-[0_10px_24px_rgba(31,43,40,0.12)] transition ${
          summary.failed > 0
            ? 'border-[var(--color-wine-soft-border)] bg-[var(--color-wine-soft)] text-[var(--color-wine-strong)]'
            : 'border-[var(--color-border-strong)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)]'
        }`}
      >
        <AlertCircleIcon size={15} />
        <span>
          {summary.failed > 0
            ? `${summary.failed} ${pluralize(summary.failed, 'falha', 'falhas')} de sincronização`
            : `${summary.pending} pendente${summary.pending === 1 ? '' : 's'}`}
        </span>
      </button>

      {open ? (
        <div className="mt-2 w-[min(92vw,420px)] rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 shadow-[0_16px_40px_rgba(31,43,40,0.16)]">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-[var(--color-text-primary)]">Sincronização pendente</p>
            <p className="text-[11px] text-[var(--color-text-secondary)]">
              {summary.total} {pluralize(summary.total, 'item', 'itens')}
            </p>
          </div>

          <div className="max-h-56 space-y-2 overflow-auto pr-1">
            {items.slice(0, 8).map((item) => (
              <div
                key={item.id}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-page)] px-2.5 py-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-text-primary)]">
                      {OUTBOX_KIND_LABEL[item.kind]} · {statusLabel[item.status]}
                    </p>
                    <p className="mt-1 text-[11px] text-[var(--color-text-secondary)]">
                      {item.lastError || 'Aguardando conexão para enviar.'}
                    </p>
                    <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
                      Tentativas: {item.retryCount}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(item.id)}
                    className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-text-secondary)]"
                  >
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              className="min-h-10 px-3 text-xs"
              onClick={() => void retryAll()}
              disabled={isRetrying}
            >
              {isRetrying ? 'Reenviando...' : 'Reenviar agora'}
            </Button>
            <Button
              variant="ghost"
              className="min-h-10 px-3 text-xs"
              onClick={clearFailed}
              disabled={summary.failed === 0}
            >
              Limpar falhas
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
